import { useEffect, useMemo, useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Target, Users, Send, CheckSquare, Inbox as InboxIcon,
  CalendarCheck, GitBranch, ShieldCheck, CreditCard, Plug, UserCog, Linkedin,
  Sun, Moon, ChevronDown, LogOut, Check, Building2, Clock, ArrowUpRight, Plus,
  Shield, ScrollText, LayoutGrid, Share2, CalendarDays, Menu, Contact, Lightbulb, UserCircle,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { NewWorkspaceDialog } from "@/components/NewWorkspaceDialog";
import { OperatorRail } from "@/components/OperatorRail";
import { TourOverlay } from "@/components/TourOverlay";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const NAV = [
  { section: "Operate", items: [
    { to: "/app", label: "Command Center", icon: LayoutDashboard, id: "dashboard", end: true },
    { to: "/app/offer", label: "Offer", icon: Target, id: "offer" },
    { to: "/app/strategy", label: "Strategy", icon: Lightbulb, id: "strategy" },
    { to: "/app/leads", label: "Leads", icon: Users, id: "leads" },
    { to: "/app/contacts", label: "Contacts", icon: Contact, id: "contacts" },
    { to: "/app/social", label: "Social", icon: Share2, id: "social" },
    { to: "/app/calendar", label: "Calendar", icon: CalendarDays, id: "calendar" },
  ]},
  { section: "Outbound", items: [
    { to: "/app/campaigns", label: "Campaigns", icon: Send, id: "campaigns" },
    { to: "/app/approval", label: "Approval", icon: CheckSquare, id: "approvals" },
    { to: "/app/inbox", label: "Inbox", icon: InboxIcon, id: "inbox" },
    { to: "/app/tasks", label: "Tasks", icon: Linkedin, id: "tasks" },
  ]},
  { section: "Revenue", items: [
    { to: "/app/pipeline", label: "Pipeline", icon: GitBranch, id: "pipeline" },
  ]},
  { section: "Admin", adminOnly: true, items: [
    { to: "/app/admin", label: "Admin Overview", icon: LayoutGrid, id: "admin", end: true },
    { to: "/app/admin/team", label: "Team", icon: UserCog, id: "team" },
    { to: "/app/admin/workspaces", label: "Workspaces", icon: Building2, id: "admin-workspaces" },
    { to: "/app/admin/billing", label: "Billing", icon: CreditCard, id: "billing" },
    { to: "/app/admin/sending", label: "Sending", icon: ShieldCheck, id: "compliance" },
    { to: "/app/admin/audit", label: "Audit", icon: ScrollText, id: "audit" },
  ]},
  { section: "Settings", items: [
    { to: "/app/settings/integrations", label: "Integrations", icon: Plug, id: "integrations" },
    { to: "/app/settings/profile", label: "Profile", icon: UserCircle, id: "profile" },
  ]},
];

function NavSections({ isAdmin, onNavigate }) {
  const groups = useMemo(() => NAV.filter((group) => !group.adminOnly || isAdmin), [isAdmin]);
  return (
    <>
      {groups.map((group) => (
        <div key={group.section}>
          <div className="px-3 mb-2 text-[10px] uppercase font-mono tracking-[0.2em] text-muted-foreground/70">{group.section}</div>
          <div className="space-y-0.5">
            {group.items.map((item) => (
              <NavLink key={item.to} to={item.to} data-testid={`nav-${item.id}`} end={item.end} onClick={onNavigate}
                className={({ isActive }) =>
                  `group flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                    isActive ? "bg-amber-500/10 text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-accent/60"
                  }`}>
                {({ isActive }) => (
                  <>
                    <item.icon className={`h-4 w-4 ${isActive ? "text-amber-500" : ""}`} strokeWidth={2} />
                    <span className="truncate">{item.label}</span>
                  </>
                )}
              </NavLink>
            ))}
          </div>
        </div>
      ))}
    </>
  );
}

export default function AppShell() {
  const { theme, toggle } = useTheme();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "owner" || user?.role === "admin";
  const [workspaces, setWorkspaces] = useState([]);
  const [active, setActive] = useState(null);
  const [org, setOrg] = useState(null);
  const [usage, setUsage] = useState(null);
  const [newWsOpen, setNewWsOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const loadWs = async () => {
    const { data } = await api.get("/workspaces");
    setWorkspaces(data);
    setActive(user?.active_workspace_id || data[0]?.id);
  };

  useEffect(() => {
    loadWs();
    api.get("/org/status").then((r) => setOrg(r.data)).catch(() => {});
    api.get("/usage").then((r) => setUsage(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // In-app scheduled-send confirmations (product mail is not wired): poll, toast, mark read.
  useEffect(() => {
    let stop = false;
    const poll = async () => {
      try {
        const { data } = await api.get("/notifications");
        if (!stop && data.length) {
          for (const n of data) {
            const fn = n.kind === "failed" ? toast.error : n.kind === "blocked" ? toast.warning : toast.success;
            fn(n.title, { description: n.body });
          }
          await api.post("/notifications/read", { ids: data.map((n) => n.id) });
        }
      } catch (e) { if (process.env.NODE_ENV !== "production") console.debug("notifications poll failed", e); }
    };
    poll();
    const iv = setInterval(poll, 30000);
    return () => { stop = true; clearInterval(iv); };
  }, []);

  const switchWs = async (id) => {
    await api.post(`/workspaces/${id}/activate`);
    setActive(id);
    window.location.reload();
  };

  const doLogout = async () => { await logout(); navigate("/"); };
  const activeWs = workspaces.find((w) => w.id === active);
  const sendMeter = usage?.meters?.find((m) => m.key === "sends");
  const sendPct = sendMeter && sendMeter.quota ? Math.round((sendMeter.used / sendMeter.quota) * 100) : 0;
  const showUsageBanner = sendMeter && sendMeter.quota && sendPct >= 80;

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-border/60 bg-card/40 sticky top-0 h-screen">
        <div className="h-16 flex items-center px-5 border-b border-border/60">
          <NavLink to="/app"><Logo /></NavLink>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-6" data-testid="app-sidebar-nav">
          <NavSections isAdmin={isAdmin} />
        </nav>
        <div className="p-3 border-t border-border/60">
          <div className="rounded-md bg-accent/40 px-3 py-2.5 text-xs">
            <div className="text-muted-foreground">Current plan</div>
            <div className="font-medium text-foreground mt-0.5">
              {org?.plan_name || "No active plan"}
            </div>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        {(org?.billing_status === "past_due" || org?.billing_status === "frozen") && (
          <div className={`px-4 sm:px-6 py-2 flex items-center justify-between gap-3 ${org.billing_status === "frozen" ? "bg-rose-600 text-white" : "bg-amber-500/15 text-foreground border-b border-amber-500/30"}`} data-testid="billing-banner">
            <span className="text-sm font-medium flex items-center gap-2">
              <CreditCard className="h-4 w-4 shrink-0" />
              {org.billing_status === "frozen"
                ? "Live sending is paused due to a failed payment. Update billing to resume — nothing was deleted."
                : "Your last payment failed. Update billing within 3 days or live sending will pause. Your data stays safe."}
            </span>
            {isAdmin ? (
              <button onClick={() => navigate("/app/admin/billing")} data-testid="billing-banner-btn"
                className={`shrink-0 rounded-md px-3 py-1 text-xs font-semibold transition-colors flex items-center gap-1 ${org.billing_status === "frozen" ? "bg-white text-rose-600 hover:bg-rose-50" : "bg-amber-500 text-black hover:bg-amber-400"}`}>
                Update billing <ArrowUpRight className="h-3.5 w-3.5" />
              </button>
            ) : (
              <span className="shrink-0 text-xs opacity-80" data-testid="billing-banner-sdr">Contact your admin to update billing.</span>
            )}
          </div>
        )}
        {showUsageBanner && (
          <div className={`px-4 sm:px-6 py-2 flex items-center justify-between gap-3 ${sendPct >= 100 ? "bg-rose-600 text-white" : "bg-amber-500/15 text-foreground border-b border-amber-500/30"}`} data-testid="usage-banner">
            <span className="text-sm font-medium flex items-center gap-2">
              <Send className="h-4 w-4" />
              {sendPct >= 100
                ? `Send quota reached (${sendMeter.used}/${sendMeter.quota}). Sending is paused until you add a pack or upgrade.`
                : `You've used ${sendPct}% of your send quota (${sendMeter.used}/${sendMeter.quota}).`}
            </span>
            <button onClick={() => navigate("/app/admin/billing")} data-testid="usage-upsell-btn"
              className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors flex items-center gap-1 ${sendPct >= 100 ? "bg-white text-rose-600 hover:bg-rose-50" : "bg-amber-500 text-black hover:bg-amber-400"}`}>
              {sendPct >= 100 ? "Add sends / upgrade" : "Add a pack"} <ArrowUpRight className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
        <header className="h-16 sticky top-0 z-40 glass flex items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
              <SheetTrigger asChild>
                <button data-testid="mobile-nav-trigger" aria-label="Open menu"
                  className="lg:hidden h-9 w-9 rounded-md border border-border/70 flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-border transition-colors">
                  <Menu className="h-5 w-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-0 flex flex-col" data-testid="mobile-nav-drawer">
                <div className="h-16 flex items-center px-5 border-b border-border/60 shrink-0">
                  <Logo />
                </div>
                <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-6" data-testid="mobile-sidebar-nav">
                  <NavSections isAdmin={isAdmin} onNavigate={() => setMobileNavOpen(false)} />
                </nav>
                <div className="p-3 border-t border-border/60 shrink-0">
                  <div className="rounded-md bg-accent/40 px-3 py-2.5 text-xs">
                    <div className="text-muted-foreground">Current plan</div>
                    <div className="font-medium text-foreground mt-0.5">
                      {org?.plan_name || "No active plan"}
                    </div>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
            <div className="lg:hidden"><Logo showText={false} /></div>
            {/* Workspace switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="workspace-switcher" className="flex items-center gap-2 rounded-md border border-border/70 px-3 py-1.5 text-sm hover:border-border transition-colors">
                  <Building2 className="h-4 w-4 text-amber-500" />
                  <span className="font-medium max-w-[160px] truncate">{activeWs?.name || "Workspace"}</span>
                  {activeWs?.sector === "public" && <span className="text-[10px] font-mono uppercase tracking-wider text-sky-500">gov</span>}
                  <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64">
                <DropdownMenuLabel className="text-xs font-mono uppercase tracking-wider text-muted-foreground">GTM Workspaces</DropdownMenuLabel>
                {workspaces.map((w) => (
                  <DropdownMenuItem key={w.id} onClick={() => switchWs(w.id)} data-testid={`ws-option-${w.sector}`} className="justify-between">
                    <span className="flex items-center gap-2">
                      {w.name}
                      {w.sector === "public" && <span className="text-[10px] font-mono uppercase text-sky-500">gov</span>}
                    </span>
                    {w.id === active && <Check className="h-4 w-4 text-amber-500" />}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setNewWsOpen(true)} data-testid="new-workspace-item" className="text-amber-500 focus:text-amber-500">
                  <Plus className="h-4 w-4 mr-2" /> New workspace
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="flex items-center gap-2">
            <button data-testid="theme-toggle-button" onClick={toggle}
              className="h-9 w-9 rounded-md border border-border/70 flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-border transition-colors">
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu" className="flex items-center gap-2 rounded-md border border-border/70 pl-1 pr-2.5 py-1 hover:border-border transition-colors">
                  <span className="h-7 w-7 rounded-full bg-amber-500/15 text-amber-500 flex items-center justify-center text-xs font-semibold">
                    {(user?.name || "U").slice(0, 1).toUpperCase()}
                  </span>
                  <span className="text-sm font-medium hidden sm:block max-w-[120px] truncate">{user?.name}</span>
                  <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="text-sm font-medium">{user?.name}</div>
                  <div className="text-xs text-muted-foreground font-normal truncate">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/app/admin/billing")}>Billing & Usage</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/app/settings/profile")}>Profile</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={doLogout} data-testid="logout-button" className="text-rose-500 focus:text-rose-500">
                  <LogOut className="h-4 w-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 min-w-0">
          <Outlet />
        </main>
      </div>
      <NewWorkspaceDialog open={newWsOpen} onOpenChange={setNewWsOpen} activate onCreated={() => window.location.reload()} />
      <OperatorRail />
      <TourOverlay />
    </div>
  );
}
